import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { WelcomeBackBanner } from "@/components/WelcomeBackBanner";
import { DomainNoticeBanner } from "@/components/DomainNoticeBanner";
import MobileBottomNav from "@/components/MobileBottomNav";
import { useAuth } from "@/context/AuthContext";
import { useGroups } from "@/context/GroupContext";
import { useI18n } from "@/context/I18nContext";
import { useSubscription } from "@/context/SubscriptionContext";
import { LANGS } from "@/lib/i18n";
import { Feather, Egg, Bird, LogOut, Cloud, CloudOff, RefreshCw, ChevronsUpDown, Plus, Copy, Check, GitBranch, Settings as SettingsIcon, Users, Globe, Pill, CreditCard, ShieldCheck, Trash2, UserCircle, NotebookPen, Megaphone, X, Star, Mail, Store } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import GroupSettingsDialog from "@/components/GroupSettingsDialog";
import GroupMembersDialog from "@/components/GroupMembersDialog";
import TextStyleMenu from "@/components/TextStyleMenu";
import NotificationsBell from "@/components/NotificationsBell";
import DeleteAccountDialog from "@/components/DeleteAccountDialog";

function SyncBadge() {
  const { t } = useI18n();
  const [status, setStatus] = useState("idle");
  const [lastTick, setLastTick] = useState(null);

  useEffect(() => {
    let mounted = true;
    const ping = async () => {
      setStatus("syncing");
      try {
        await api.get("/schedule/sync");
        if (!mounted) return;
        setStatus("online");
        setLastTick(new Date());
      } catch {
        if (!mounted) return;
        setStatus("offline");
      }
    };
    ping();
    const id = setInterval(ping, 8000);
    return () => { mounted = false; clearInterval(id); };
  }, []);

  const Icon = status === "offline" ? CloudOff : status === "syncing" ? RefreshCw : Cloud;
  const color = status === "offline" ? "text-danger bg-danger/10" : "text-sync-on bg-sync-on/10";
  const label = status === "offline" ? t("sync.offline") : status === "syncing" ? t("sync.syncing") : t("sync.synced");

  return (
    <div
      className={`flex items-center gap-2 ${color} px-3 py-1.5 rounded-full text-xs font-semibold uppercase tracking-widest`}
      data-testid="sync-indicator"
    >
      <Icon className={`w-3.5 h-3.5 ${status === "syncing" ? "animate-spin" : ""}`} />
      <span>{label}</span>
      {lastTick && status === "online" && (
        <span className="hidden sm:inline opacity-70 normal-case tracking-normal">
          {lastTick.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
        </span>
      )}
    </div>
  );
}

function LanguageToggle() {
  const { lang, changeLanguage, t } = useI18n();
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-1.5 bg-cream-2 hover:bg-app-border px-2.5 py-1.5 rounded-lg transition-colors text-xs font-semibold"
          data-testid="language-toggle"
          title={t("common.language")}
        >
          <Globe className="w-3.5 h-3.5 text-espresso/70" />
          <span className="uppercase tracking-widest">{lang}</span>
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-olive">
          {t("common.language")}
        </DropdownMenuLabel>
        {Object.entries(LANGS).map(([code, label]) => (
          <DropdownMenuItem
            key={code}
            onClick={() => changeLanguage(code)}
            className={code === lang ? "bg-olive/10" : ""}
            data-testid={`lang-${code}`}
          >
            <span className="flex-1">{label}</span>
            {code === lang && <Check className="w-4 h-4 text-olive" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function GroupSwitcher({ onOpenSettings, onOpenMembers }) {
  const { groups, activeGroup, switchGroup } = useGroups();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);

  if (!activeGroup) return null;

  const copyCode = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(activeGroup.invite_code);
      setCopied(true);
      toast.success(t("group.copied"));
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="flex items-center gap-2 bg-cream-2 hover:bg-app-border px-3 py-1.5 rounded-lg transition-colors max-w-[200px]"
          data-testid="group-switcher"
        >
          <div className="text-left min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-espresso/50 leading-none">{t("group.label")}</div>
            <div className="text-sm font-semibold text-espresso truncate">{activeGroup.name}</div>
          </div>
          <ChevronsUpDown className="w-4 h-4 text-espresso/40 flex-shrink-0" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72">
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-olive">
          {t("group.switch")}
        </DropdownMenuLabel>
        {(groups || []).map((g) => (
          <DropdownMenuItem
            key={g.id}
            onClick={() => switchGroup(g.id)}
            className={g.id === activeGroup.id ? "bg-olive/10" : ""}
            data-testid={`switch-group-${g.id}`}
          >
            <div className="flex-1 min-w-0 flex items-center gap-2">
              <span
                className="w-3 h-3 rounded-full border border-app-border"
                style={{ backgroundColor: g.accent_color || "#6B8E4F" }}
              />
              <div className="min-w-0">
                <div className="font-semibold truncate">{g.name}</div>
                <div className="text-[10px] font-mono text-espresso/50">{g.invite_code} · {g.member_count}p · {g.incubation_days || 21}d</div>
              </div>
            </div>
            {g.id === activeGroup.id && <Check className="w-4 h-4 text-olive" />}
          </DropdownMenuItem>
        ))}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-olive">
          {t("group.invite_code")}
        </DropdownMenuLabel>
        <DropdownMenuItem onClick={copyCode} data-testid="copy-invite-code">
          <span className="font-mono font-bold text-olive tracking-widest">{activeGroup.invite_code}</span>
          <span className="ml-auto text-xs flex items-center gap-1">
            {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
            {copied ? t("group.copied") : t("group.copy")}
          </span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onOpenMembers} data-testid="open-members">
          <Users className="w-4 h-4 mr-2" />
          {t("group.members")}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={onOpenSettings} data-testid="open-settings">
          <SettingsIcon className="w-4 h-4 mr-2" />
          {t("group.settings")}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => navigate("/groups")} data-testid="manage-groups">
          <Plus className="w-4 h-4 mr-2" />
          {t("group.new_join")}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { activeGroup } = useGroups();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [membersOpen, setMembersOpen] = useState(false);
  const [deleteAccountOpen, setDeleteAccountOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);

  useEffect(() => {
    const fetchUnread = () => {
      api.get("/messages/unread-count").then(({ data }) => setUnreadMessages(data.count || 0)).catch(() => {});
    };
    fetchUnread();
    const iv = setInterval(fetchUnread, 30000);
    return () => clearInterval(iv);
  }, []);

  // Apply group color theme via CSS variables
  useEffect(() => {
    const root = document.documentElement;
    if (activeGroup) {
      root.style.setProperty("--aviary-bg", activeGroup.background_color || "#F7F9F6");
      root.style.setProperty("--aviary-accent", activeGroup.accent_color || "#6B8E4F");
    }
  }, [activeGroup]);

  const onLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  const linkBase = "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors";
  const linkActive = "bg-olive text-white";
  const linkInactive = "text-espresso/80 hover:bg-cream-2";

  return (
    <div className="min-h-screen" style={{ backgroundColor: activeGroup?.background_color || "#F7F9F6" }}>
      <header
        className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b border-app-border h-16 flex items-center px-4 sm:px-6 gap-3"
        data-testid="app-header"
        data-print-hide
      >
        <div className="flex items-center gap-2">
          <div
            className="w-9 h-9 rounded-xl grid place-items-center text-white"
            style={{ backgroundColor: activeGroup?.accent_color || "#6B8E4F" }}
          >
            <Feather className="w-4 h-4" />
          </div>
          <div className="leading-tight hidden sm:block">
            <div className="font-heading font-semibold text-espresso">{t("app.brand")}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-espresso/50">{t("app.tagline")}</div>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-1 ml-4" data-testid="primary-nav">
          <NavLink to="/couples" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-couples">
            <Egg className="w-4 h-4" /> {t("nav.couples")}
          </NavLink>
          <NavLink to="/birds" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-birds">
            <Bird className="w-4 h-4" /> {t("nav.birds")}
          </NavLink>
          <NavLink to="/pedigree" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-pedigree">
            <GitBranch className="w-4 h-4" /> {t("nav.pedigree")}
          </NavLink>
          <NavLink to="/courses" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-courses">
            <Pill className="w-4 h-4" /> {t("nav.courses")}
          </NavLink>
          <NavLink to="/remarks" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-remarks">
            <NotebookPen className="w-4 h-4" /> {t("nav.remarks")}
          </NavLink>
          <NavLink to="/market" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-market">
            <Store className="w-4 h-4" /> {t("nav.market")}
          </NavLink>
          <NavLink to="/reviews" className={({ isActive }) => `${linkBase} ${isActive ? linkActive : linkInactive}`} data-testid="nav-reviews">
            <Star className="w-4 h-4" /> {t("nav.reviews")}
          </NavLink>
        </nav>

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <GroupSwitcher onOpenSettings={() => setSettingsOpen(true)} onOpenMembers={() => setMembersOpen(true)} />
          <LanguageToggle />
          <TextStyleMenu />
          <NotificationsBell />
          <SyncBadge />
          <SubscriptionBadge />
          <div className="hidden lg:block text-right">
            <div className="text-xs font-semibold text-espresso truncate max-w-[140px]">{user?.email}</div>
            <div className="text-[10px] uppercase tracking-widest text-espresso/50">{user?.role}</div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="relative p-2 rounded-lg hover:bg-cream-2 text-espresso/70 hover:text-espresso transition-colors"
                data-testid="user-menu-trigger"
                title="Account"
              >
                <UserCircle className="w-5 h-5" />
                {unreadMessages > 0 && (
                  <span className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-red-500 border-2 border-white" data-testid="user-menu-unread-dot" />
                )}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="text-[10px] uppercase tracking-widest text-olive">
                {user?.email}
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => navigate("/settings")}
                data-testid="open-account-settings"
                className="cursor-pointer"
              >
                <SettingsIcon className="w-4 h-4 mr-2" />
                {t("nav.settings")}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => navigate("/messages")}
                data-testid="open-messages"
                className="cursor-pointer"
              >
                <Mail className="w-4 h-4 mr-2" />
                {t("nav.messages")}
                {unreadMessages > 0 && (
                  <span className="ml-auto rtl:ml-0 rtl:mr-auto bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5" data-testid="messages-unread-badge">
                    {unreadMessages}
                  </span>
                )}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onLogout} data-testid="logout-button" className="cursor-pointer">
                <LogOut className="w-4 h-4 mr-2" />
                {t("nav.logout") || "Logout"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => setDeleteAccountOpen(true)}
                className="cursor-pointer text-red-700 focus:text-red-800 focus:bg-red-50"
                data-testid="open-delete-account"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {t("auth.delete_account")}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <MobileBottomNav />

      <AnnouncementBanner />
      <DomainNoticeBanner />
      <WelcomeBackBanner />

      <main className="max-w-[1400px] mx-auto p-4 sm:p-6 lg:p-8 pb-24 md:pb-12 print-area">
        <Outlet />
      </main>

      <footer
        className="border-t border-app-border bg-white/60 px-4 py-3 text-center text-[11px] text-espresso/60 mb-16 md:mb-0 no-print"
        data-testid="app-footer"
      >
        {t("footer.copyright").replace("{year}", new Date().getFullYear())}
      </footer>

      <GroupSettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      <GroupMembersDialog open={membersOpen} onOpenChange={setMembersOpen} />
      <DeleteAccountDialog open={deleteAccountOpen} onOpenChange={setDeleteAccountOpen} />
    </div>
  );
}

function AnnouncementBanner() {
  const { lang } = useI18n();
  const [ann, setAnn] = useState(null);
  useEffect(() => {
    api
      .get("/announcement")
      .then(({ data }) => {
        const a = data.announcement;
        if (a && localStorage.getItem("dismissed_announcement_id") !== a.id) setAnn(a);
      })
      .catch(() => {});
  }, []);
  if (!ann) return null;
  const text =
    lang === "ar"
      ? ann.message_ar || ann.message_en || ann.message
      : ann.message_en || ann.message || ann.message_ar;
  return (
    <div
      className="bg-olive text-white px-4 sm:px-6 py-2.5 flex items-start gap-2.5 text-sm"
      data-testid="announcement-banner"
      data-print-hide
    >
      <Megaphone className="w-4 h-4 mt-0.5 shrink-0" />
      <div className="flex-1 whitespace-pre-wrap leading-snug">{text}</div>
      <button
        type="button"
        onClick={() => {
          localStorage.setItem("dismissed_announcement_id", ann.id);
          setAnn(null);
        }}
        className="shrink-0 opacity-80 hover:opacity-100 transition-opacity"
        aria-label="Dismiss"
        data-testid="announcement-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function SubscriptionBadge() {
  const { sub } = useSubscription();
  const { user } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  if (!sub) return null;
  const isTrial = sub.status === "trial";
  const isActive = sub.is_active;
  const tone =
    !isActive ? "bg-danger/15 text-danger" :
    isTrial ? "bg-amber-100 text-amber-800" :
    "bg-olive/15 text-olive";
  const label = !isActive ? "Renew" : isTrial ? `Trial · ${sub.days_left}d` : `${sub.days_left}d`;
  return (
    <div className="flex items-center gap-1">
      <button
        onClick={() => navigate("/subscription")}
        className={`flex flex-col items-center gap-0.5 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded-lg ${tone}`}
        data-testid="subscription-badge"
        title="Subscription"
      >
        <span className="flex items-center gap-1">
          <CreditCard className="w-3 h-3" />
          {label}
        </span>
        <span className="text-[8px] tracking-[0.15em] opacity-80">{t("sub.badge_label")}</span>
      </button>
      {user?.role === "admin" && (
        <button
          onClick={() => navigate("/admin/subscriptions")}
          className="bg-cream-2 hover:bg-app-border p-1.5 rounded-lg transition-colors"
          data-testid="admin-subs-link"
          title="Subscriptions admin"
        >
          <ShieldCheck className="w-3.5 h-3.5 text-espresso/70" />
        </button>
      )}
    </div>
  );
}
